import COS from 'cos-js-sdk-v5'
import { getCredentialApi } from '@/api/file/index' // 确保你的 API 路径正确

// 定义接口类型
interface Credentials {
  tmpSecretId: string
  tmpSecretKey: string
  sessionToken: string
}

interface AuthResponse {
  credentials: Credentials
  startTime: number
  expiredTime: number
}

interface CosConfig {
  response: AuthResponse
  protocol: 'http' | 'https'
  domain: string
  region: string
  bucketName: string
}

let cosInstance: COS | null = null
let cosConfig: CosConfig | null = null
let credentialExpireTime = 0
let initPromise: Promise<void> | null = null

/**
 * 初始化 COS 实例（带并发锁，防止重复请求密钥）
 */
async function initCos() {
  const now = Math.floor(Date.now() / 1000)
  
  // 如果实例存在且凭证未过期，直接返回
  if (cosInstance && credentialExpireTime > now) {
    return
  }

  // 如果正在初始化中，等待当前进程完成
  if (initPromise) {
    return await initPromise
  }

  // 创建初始化锁
  initPromise = (async () => {
    try {
      const res = await getCredentialApi()
      cosConfig = res as CosConfig
      
      // 设置过期时间（提前 60 秒刷新，保证稳定性）
      credentialExpireTime = cosConfig.response.expiredTime - 60

      // 创建 COS 实例
      cosInstance = new COS({
        getAuthorization: (_, callback) => {
          const { credentials, startTime, expiredTime } = cosConfig!.response
          callback({
            TmpSecretId: credentials.tmpSecretId,
            TmpSecretKey: credentials.tmpSecretKey,
            SecurityToken: credentials.sessionToken,
            StartTime: startTime,
            ExpiredTime: expiredTime,
          })
        },
      })
    } catch (error) {
      console.error('COS 初始化失败:', error)
      throw error
    } finally {
      initPromise = null
    }
  })()

  await initPromise
}

/**
 * 获取单张图片的鉴权 URL
 * @param key 图片在 COS 中的路径 (例如: images/2024/test.jpg)
 * @param sign 是否需要签名 (默认 true)
 * @returns 签名的 URL
 */
export async function getSignedUrl(key: string, sign = true): Promise<string> {
  // 1. 确保 COS 已初始化
  await initCos()

  if (!cosInstance || !cosConfig) {
    throw new Error('COS 实例未初始化')
  }

  // 2. 处理 Key (如果传入的是完整 URL，提取路径)
  let processedKey = key
  if (key.startsWith('http')) {
    const match = key.match(/https?:\/\/[^\/]+(\/.*)/)
    if (match?.[1]) {
      processedKey = match[1].slice(1)
    }
  }

  // 3. 调用 SDK 获取签名 URL
  return new Promise((resolve, reject) => {
    cosInstance!.getObjectUrl(
      {
        Bucket: cosConfig!.bucketName,
        Region: cosConfig!.region,
        Key: processedKey,
        Domain: cosConfig!.domain,
        Protocol: cosConfig!.protocol,
        Sign: sign,
      },
      (err, data) => {
        if (err) {
          reject(err)
        } else {
          resolve(data.Url)
        }
      }
    )
  })
}